/* eslint-disable no-param-reassign,no-continue,no-use-before-define */
import { get, set, difference, concat, pick, isEqual } from 'lodash';

import { LOCATIONS_ALL_ID } from '../constants';
import { getDefaultBarberLocationInitialValues, getInitialValuesFromShop } from './formValuesHelpers';
import { INDETERMINATE } from '../../../../../common/constants';
import {
  prepareDaysForSaving,
  prepareMinsForSaving,
  prepareScheduleForSaving,
} from '../../../../../common/helpers/schedules';
import { prepareModel } from '../../../formBarbers/barberForm/components/BarberForm';
import { minServiceLenModel } from '../../../../../common/helpers/modelSettings';
import { hoursMinutesFields } from './constants';
import { SHOP_TYPE as SHOP_KINDS } from '../../../../shops/constants';

const isObject = value => !!(value && typeof value === 'object' && !Array.isArray(value));

const prepareForApi = (diffValues, shop) => {
  let result = prepareModel(diffValues);

  hoursMinutesFields.forEach((timeKey) => {
    const timeValue = get(result, timeKey);
    if (timeValue) {
      set(result, timeKey, prepareMinsForSaving(timeValue));
    }
  });

  if (result.schedules) {
    result.schedules = prepareScheduleForSaving(result.schedules);
  }

  if (result.days_on) {
    result.days_on = prepareDaysForSaving(result.days_on);
  }

  if (result.days_off) {
    result.days_off = prepareDaysForSaving(result.days_off);
  }

  result = minServiceLenModel(result, shop.minInterval);

  delete result.avatar;

  return result;
};

const keysToIgnoreForUpdatePatch = [
  'avatar',
  'photos',
  'commissionTiers',
  'oldRoutes',
  'productCommissionTiers',
  'days_off',
  'services',
  'assignedServices',
];

const keysForFullObjectDiff = [
  'days_on',
  'notifications',
  'customerReminder',
];

const makeDiffTime = (
  formValues = {},
  initialValues = {},
  diffObject = {},
  keyPrefix = ''
) => {
  if (formValues.hours !== get(initialValues, `${keyPrefix}.hours`)
    || formValues.minutes !== get(initialValues, `${keyPrefix}.minutes`)
  ) {
    set(diffObject, keyPrefix, { hours: formValues.hours, minutes: formValues.minutes });
  }
  return diffObject;
};

const getSchedulesForComparing = (formValues) => {
  const formValueSchedules = { current: formValues.schedules.current };
  if (formValues.schedules.current.endDate && formValues.schedules.future && formValues.schedules.future.startDate) {
    formValueSchedules.future = formValues.schedules.future;
  }

  return formValueSchedules;
};

const makeDiffSchedules = (formValues, initialValues, diffObject = {}) => {
  const currentSchedules = getSchedulesForComparing(formValues);
  const initialSchedules = getSchedulesForComparing(initialValues);

  if (!isEqual(currentSchedules, initialSchedules)) {
    diffObject.schedules = currentSchedules;
  }

  return diffObject;
};

const makeDiffFullObject = (
  formValues = {},
  initialValues = {},
  diffObject = {},
  keyPrefix = ''
) => {
  const currentValue = get(formValues, keyPrefix);
  const initialValue = get(initialValues, keyPrefix);
  if (!isEqual(currentValue, initialValue)) {
    set(diffObject, keyPrefix, currentValue);
  }
};

const makeDiffObject = (
  formValues = {},
  initialValues = {},
  diffObject = {},
  keyPrefix = ''
) => {
  if (isObject(formValues)) {
    const formValueObjectEntries = Object.entries(formValues);

    for (let i = 0; i < formValueObjectEntries.length; i += 1) {
      const [formValueKey, formValue] = formValueObjectEntries[i];
      const fullKeyPath = `${keyPrefix}${keyPrefix === '' ? '' : '.'}${formValueKey}`;

      if (fullKeyPath === 'shopId') {
        diffObject.shopId = formValue;
        continue;
      }

      if (fullKeyPath === 'services') {
        const updatedServices = Object.values(formValue)
          .map(({ assigned, id }) => (assigned ? { id } : false))
          .filter(Boolean);
        const initialServices = Object.values(initialValues.services)
          .map(({ assigned, id }) => (assigned ? { id } : false))
          .filter(Boolean);

        if (isEqual(updatedServices, initialServices)) {
          continue;
        }

        diffObject.services = updatedServices;

        continue;
      }

      if (keysToIgnoreForUpdatePatch.includes(fullKeyPath)) {
        continue;
      }

      if (fullKeyPath === 'schedules') {
        makeDiffSchedules(formValues, initialValues, diffObject);
        continue;
      }

      if (keysForFullObjectDiff.includes(fullKeyPath)) {
        makeDiffFullObject(formValues, initialValues, diffObject, fullKeyPath);
        continue;
      }

      if (hoursMinutesFields.includes(fullKeyPath)) {
        makeDiffTime(formValue, initialValues, diffObject, fullKeyPath);
        continue;
      }

      if (isObject(formValue)) {
        makeDiffObject(formValue, initialValues, diffObject, fullKeyPath);
        continue;
      }

      if (formValue !== get(initialValues, fullKeyPath)) {
        set(diffObject, fullKeyPath, formValue);
      }
    }
  } else if (formValues !== get(initialValues, keyPrefix)) {
    set(diffObject, keyPrefix, formValues);
  }

  return diffObject;
};

export const getUpdatedBarberValues = (formValues, initialValues, brandShops) => {
  const updatePatch = [];

  Object.keys(formValues).forEach((locationId) => {
    if (locationId !== LOCATIONS_ALL_ID) {
      const locationDiff = makeDiffObject(formValues[locationId], initialValues[locationId]);
      if (Object.keys(locationDiff).length > 1) {
        updatePatch.push(prepareForApi(locationDiff, brandShops.find(shop => shop.id === locationId)));
      }
    }
  });

  return updatePatch;
};

const sharedProfileFields = [
  'firstName',
  'lastName',
  'color',
  'username',
  'email',
  'phone',
  'instagram',
  'personnelId',
];

const useDefaultsForNewLocationFields = ['schedules', 'days_on', 'days_off'];

const setSharedProfileFieldsToNewLocation = (newLocationDefaults, allLocationsValues) => {
  sharedProfileFields.forEach(
    sharedProfileKey => set(newLocationDefaults, sharedProfileKey, get(allLocationsValues, sharedProfileKey))
  );
};

const setHoursMinsField = (defaultValue, allLocationsValues, newLocationDefaults, fieldKey) => {
  const allLocationsValue = get(allLocationsValues, fieldKey);
  set(
    newLocationDefaults,
    fieldKey,
    (allLocationsValue.hours === INDETERMINATE || allLocationsValue.minutes === INDETERMINATE)
      ? defaultValue
      : allLocationsValue
  );
};

const makeNewLocationDefaultsObject = (
  defaults = {},
  allLocationsValues = {},
  newLocationDefaults = {},
  keyPrefix = ''
) => {
  if (isObject(defaults)) {
    const defaultsObjectEntries = Object.entries(defaults);

    if (keyPrefix === '') {
      setSharedProfileFieldsToNewLocation(newLocationDefaults, allLocationsValues);
    }

    for (let i = 0; i < defaultsObjectEntries.length; i += 1) {
      const [defaultValueKey, defaultValue] = defaultsObjectEntries[i];
      const fullKeyPath = `${keyPrefix}${keyPrefix === '' ? '' : '.'}${defaultValueKey}`;

      if (sharedProfileFields.includes(fullKeyPath)) {
        continue;
      }

      if (hoursMinutesFields.includes(fullKeyPath)) {
        setHoursMinsField(defaultValue, allLocationsValues, newLocationDefaults, fullKeyPath);
        continue;
      }

      if (useDefaultsForNewLocationFields.includes(fullKeyPath)) {
        set(newLocationDefaults, fullKeyPath, get(defaults, fullKeyPath));
        continue;
      }

      if (isObject(defaultValue)) {
        makeNewLocationDefaultsObject(defaultValue, allLocationsValues, newLocationDefaults, fullKeyPath);
        continue;
      }

      const allLocationsValue = get(allLocationsValues, fullKeyPath);
      if (allLocationsValue === INDETERMINATE) {
        set(newLocationDefaults, fullKeyPath, defaultValue);
      } else {
        set(newLocationDefaults, fullKeyPath, allLocationsValue);
      }
    }
  } else if (get(allLocationsValues, keyPrefix) === INDETERMINATE) {
    set(newLocationDefaults, keyPrefix, defaults);
  } else {
    set(newLocationDefaults, keyPrefix, get(allLocationsValues, keyPrefix));
  }

  return newLocationDefaults;
};

const populateValuesForAddedLocation = (allLocationsValues, addedLocationId, addedShop) => {
  const defaults = {
    ...getDefaultBarberLocationInitialValues(
      { minInterval: addedShop.minInterval, shopSchedules: addedShop.schedules }
    ),
    ...getInitialValuesFromShop(addedShop),
  };

  const result = makeNewLocationDefaultsObject(defaults, allLocationsValues);

  if (addedShop.kind === SHOP_KINDS.rental) {
    result.permissions.engage.view = false;
  }

  result.shopId = addedLocationId;
  delete result.id;

  return prepareForApi(result, addedShop);
};

export const getLocationsUpdate = (selectedLocations, formValues, brandShops) => {
  const selectedLocationIds = selectedLocations.map(locationOption => locationOption.id);
  const currentBarberLocationIds = Object.keys(formValues).filter(locationId => locationId !== LOCATIONS_ALL_ID);

  const addedLocations = difference(selectedLocationIds, currentBarberLocationIds);
  const removedLocations = difference(currentBarberLocationIds, selectedLocationIds);

  const allLocationsValues = formValues[LOCATIONS_ALL_ID] || formValues[currentBarberLocationIds[0]];
  return concat(
    addedLocations.map(addedLocationId => populateValuesForAddedLocation(
      allLocationsValues,
      addedLocationId,
      brandShops.find(shop => shop.id === addedLocationId),
    )),
    removedLocations.map(removedLocationId => ({
      shopId: removedLocationId,
      deleted: true,
    })),
  );
};

const getNewBarberProfileValues = formValues => pick(formValues[LOCATIONS_ALL_ID], [
  'firstName',
  'lastName',
  'phone',
  'email',
  'personnelId',
  'color',
  'username',
  'userEnabled',
]);

export const getNewBarberValues = (selectedLocations, formValules, brandShops) => {
  const addedLocationIds = selectedLocations.map(locationOption => locationOption.id);

  return addedLocationIds.map((addedLocationId) => {
    const addedShop = brandShops.find(shop => shop.id === addedLocationId);

    const addedLocationValues = {
      ...getDefaultBarberLocationInitialValues(
        { minInterval: addedShop.minInterval, shopSchedules: addedShop.schedules }
      ),
      ...getInitialValuesFromShop(addedShop),
      ...getNewBarberProfileValues(formValules),
      shopId: addedLocationId,
    };

    return prepareForApi(addedLocationValues, addedShop);
  });
};
